/**
 * **C12 · The family fulfillment statement** (Rebuild 59 `Documents.dc.html` #c12; F17, ADR 0104):
 * one letter page a school family — the commitment, what we have for the family credit by credit
 * with its source and who recorded it, the total in credits and the balance in the engine's own
 * words (canvas defect 334), the ruling where the year was disputed (canvas defect 336), the
 * reply-by sentence and the Festival Director's line. Laid out by the `fulfillment_page` engine
 * from Family fulfillment's own read for the one family, under the door that read is under, so the
 * statement and the part say one thing; the finding never reaches the page. Signed by the Festival
 * Director from Roles — blank for a hand unless Roles names exactly one. On the print log;
 * measured against the drawing by `tools/fulfillment-fidelity.ts`.
 */
import type { ReactNode } from 'react';
import { fulfillmentPage, type StatementFamily } from '@cider/domain/engines';
import { dayOf, formatTime } from '@cider/ui';
import { useLoad, type Paged } from '../views/money/shared.js';
import { holder, type RoleRow } from './aod-package.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';

/** A credit's line as Family fulfillment answers it: instants ISO with Z, null for no data. */
interface Line {
  quarters: number;
  who: string | null;
  what: string | null;
  day: string | null;
  startsAt: string | null;
  endsAt: string | null;
  inBy: string | null;
  movedTo: string | null;
  recordedBy: string | null;
  recordedOn: string | null;
  note: string | null;
}
/** A family as the read answers it, as much of it as the statement prints. */
interface Family extends Omit<StatementFamily, 'sources'> {
  familyId: string;
  sources: (Omit<StatementFamily['sources'][number], 'lines'> & { lines: Line[] })[];
}
interface Answer extends Paged<Family> {
  /** The day before the shortfall letters go, YYYY-MM-DD; null while Year settings has none. */
  replyBy: string | null;
  writeTo: string;
}

/** `12:00 PM` → `12 PM`: a shift's hours as the drawing writes them, through the one formatter. */
const hour = (iso: string): string => formatTime(iso).replace(':00', '');

/** What the engine is handed of the family: its own fields and its hours in words — no finding. */
function statementFamily(f: Family): StatementFamily {
  return {
    name: f.name,
    students: f.students,
    status: f.status,
    quarters: f.quarters,
    toFulfill: f.toFulfill,
    says: f.says,
    missing: f.missing,
    ruling: f.ruling && {
      disputeNo: f.ruling.disputeNo,
      creditResult: f.ruling.creditResult,
      ruledBy: f.ruling.ruledBy,
      ruledOn: f.ruling.ruledOn,
    },
    sources: f.sources.map((g) => ({
      kind: g.kind,
      label: g.label,
      quarters: g.quarters,
      lines: g.lines.map((l) => ({
        quarters: l.quarters,
        who: l.who,
        what: l.what,
        day: l.day,
        hours: l.startsAt && l.endsAt ? `${hour(l.startsAt)} – ${hour(l.endsAt)}` : null,
        inBy: l.inBy,
        movedTo: l.movedTo,
        recordedBy: l.recordedBy,
        recordedOn: l.recordedOn,
        note: l.note,
      })),
    })),
  };
}

export function FulfillmentStatement({
  year,
  familyId,
}: {
  year: number;
  familyId: string;
}): ReactNode {
  const read = useLoad<Answer>(
    `/volunteers/fulfillment?year=${String(year)}&familyId=${encodeURIComponent(familyId)}`,
  );
  // Signed in the festival year's name. A volunteer chair may print it but not read Roles: the
  // line is then left blank for a hand.
  const roles = useLoad<RoleRow[]>(`/admin/roles/year?year=${String(year)}`);
  const answer = read.data;
  const family = answer?.rows.find((r) => r.familyId === familyId) ?? null;
  const ready = family !== null && (roles.data !== null || roles.error !== null);
  usePrinted('doc-fulfillment', ready, { ref: familyId, year });
  if (read.error) return <p role="alert">{read.error}</p>;
  if (answer && family === null)
    return <p role="alert">This family is not one of {String(year)}’s school families.</p>;
  if (family === null || !answer || !ready) return <p>Reading the figures…</p>;
  const page = fulfillmentPage({
    year,
    read: {
      family: statementFamily(family),
      replyBy: answer.replyBy,
      writeTo: answer.writeTo,
    },
    day: dayOf(),
    director: holder(roles.data, 'director'),
  });
  return <DrawnPages pages={[page]} />;
}
